// Pure ordering logic for search results — which sort options a retrieval
// mode supports, how to apply one (in either direction) to a flat list or a
// grouped list, and the human-readable labels for both.

import type { ResultGroup, RetrievalMode, SearchResult, SortDirection, SortOption } from './types';

export const SORT_OPTION_LABELS: Record<SortOption, string> = {
  relevance: 'Relevance',
  date: 'Date',
  title: 'Title',
};

// Labelled as plain-English opposites per option, not generic ascending/descending.
export const SORT_DIRECTION_LABELS: Record<SortOption, Record<SortDirection, string>> = {
  relevance: { desc: 'Best match first', asc: 'Least relevant first' },
  date: { desc: 'Newest first', asc: 'Oldest first' },
  title: { asc: 'A–Z', desc: 'Z–A' },
};

const DEFAULT_SORT_DIRECTIONS: Record<SortOption, SortDirection> = {
  relevance: 'desc',
  date: 'desc',
  title: 'asc',
};

const SEMANTIC_SORT_OPTIONS: SortOption[] = ['relevance', 'date', 'title'];
const FILTER_TAG_SORT_OPTIONS: SortOption[] = ['date', 'title'];

/** Sort options the given retrieval mode can support. */
export function getAvailableSortOptions(mode: RetrievalMode | null | undefined): SortOption[] {
  return mode === 'filter_tags' ? FILTER_TAG_SORT_OPTIONS : SEMANTIC_SORT_OPTIONS;
}

/** The sort a fresh search should open on, matching the order the backend already returns results in. */
export function getDefaultSortOption(mode: RetrievalMode | null | undefined): SortOption {
  return mode === 'filter_tags' ? 'title' : 'relevance';
}

/** The direction a sort option opens on when first selected, matching the backend's own default ordering. */
export function getDefaultSortDirection(sortBy: SortOption): SortDirection {
  return DEFAULT_SORT_DIRECTIONS[sortBy];
}

function compareByTitle(a: SearchResult, b: SearchResult, direction: SortDirection = 'asc'): number {
  const result = (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
  return direction === 'asc' ? result : -result;
}

/** Orders by a nullable numeric value, always pushing missing values to the end regardless of direction, then falls back to title. */
function compareNullableNumeric(
  aValue: number | null,
  bValue: number | null,
  direction: SortDirection,
  a: SearchResult,
  b: SearchResult
): number {
  if (aValue != null && bValue != null) return direction === 'desc' ? bValue - aValue : aValue - bValue;
  if ((aValue != null) !== (bValue != null)) return aValue != null ? -1 : 1; // missing values sort last either way
  return compareByTitle(a, b);
}

function compareByDate(a: SearchResult, b: SearchResult, direction: SortDirection): number {
  const aTime = Date.parse(a.metadata?.created ?? '');
  const bTime = Date.parse(b.metadata?.created ?? '');
  return compareNullableNumeric(
    Number.isNaN(aTime) ? null : aTime,
    Number.isNaN(bTime) ? null : bTime,
    direction,
    a,
    b
  );
}

function compareByRelevance(a: SearchResult, b: SearchResult, direction: SortDirection): number {
  return compareNullableNumeric(a.reranking_score, b.reranking_score, direction, a, b);
}

const COMPARATORS: Record<
  SortOption,
  (a: SearchResult, b: SearchResult, direction: SortDirection) => number
> = {
  relevance: compareByRelevance,
  date: compareByDate,
  title: compareByTitle,
};

/** Returns a new array of results ordered by the chosen criterion and direction, breaking ties by title. */
export function sortResults(
  results: SearchResult[],
  sortBy: SortOption,
  direction: SortDirection
): SearchResult[] {
  return [...results].sort((a, b) => COMPARATORS[sortBy](a, b, direction));
}

/** Applies sortResults within every group's results, recursively, leaving group order untouched. */
export function sortGroupedResults(
  groups: ResultGroup[],
  sortBy: SortOption,
  direction: SortDirection
): ResultGroup[] {
  return groups.map((group) => ({
    ...group,
    results: sortResults(group.results, sortBy, direction),
    children: sortGroupedResults(group.children, sortBy, direction),
  }));
}
